export interface PressureBrickSample {
  readonly y: number;
  readonly height: number;
  readonly bottom?: number;
  readonly active?: boolean;
}

export function resolveBrickDropDistance(rowStep: number, dropRows: number): number {
  return Math.max(0, rowStep) * Math.max(0, Math.floor(dropRows));
}

export function resolveContinuousPressureDistance(
  rowStep: number,
  dropRows: number,
  intervalMs: number,
  deltaMs: number,
): number {
  const dropDistance = resolveBrickDropDistance(rowStep, dropRows);
  const interval = Math.max(0, intervalMs);
  if (dropDistance <= 0 || interval <= 0) return 0;
  return dropDistance * (Math.max(0, deltaMs) / interval);
}

export interface PressureProgressState {
  readonly progress: number;
  readonly remainingMs: number;
  readonly totalMs: number;
}

export type PressureThreat = "none" | "warning" | "danger";

export interface PressureThreatThresholds {
  readonly warningRows: number;
  readonly dangerRows: number;
}

export function resolvePressureProgress(elapsedMs: number, totalMs: number): PressureProgressState {
  const total = Math.max(0, totalMs);
  if (total <= 0) return { progress: 0, remainingMs: 0, totalMs: 0 };
  const progress = Math.min(1, Math.max(0, elapsedMs / total));
  return {
    progress,
    remainingMs: Math.max(0, total - elapsedMs),
    totalMs: total,
  };
}

export function hasBrickReachedLauncher(
  bricks: readonly PressureBrickSample[],
  launcherY: number,
): boolean {
  return bricks.some((brick) => {
    if (brick.active === false) return false;
    return pressureBrickBottom(brick) >= launcherY;
  });
}

export function willBrickReachLauncherAfterDrop(
  brick: PressureBrickSample,
  launcherY: number,
  dropDistance: number,
): boolean {
  if (brick.active === false) return false;
  if (dropDistance <= 0) return false;
  return pressureBrickBottom(brick) + dropDistance >= launcherY;
}

export function resolveLowestPressureDangerBottom(
  bricks: readonly PressureBrickSample[],
  launcherY: number,
  dropDistance: number,
): number | null {
  let lowestBottom = Number.NEGATIVE_INFINITY;
  for (const brick of bricks) {
    if (!willBrickReachLauncherAfterDrop(brick, launcherY, dropDistance)) continue;
    lowestBottom = Math.max(lowestBottom, pressureBrickBottom(brick));
  }
  return Number.isFinite(lowestBottom) ? lowestBottom : null;
}

export function resolvePressureThreat(
  bricks: readonly PressureBrickSample[],
  launcherY: number,
  rowStep: number,
  thresholds: PressureThreatThresholds,
): PressureThreat {
  if (rowStep <= 0) return "none";

  let lowestBrickBottom = Number.NEGATIVE_INFINITY;
  for (const brick of bricks) {
    if (brick.active === false) continue;
    lowestBrickBottom = Math.max(lowestBrickBottom, pressureBrickBottom(brick));
  }
  if (!Number.isFinite(lowestBrickBottom)) return "none";

  const distance = launcherY - lowestBrickBottom;
  if (distance <= rowStep * Math.max(0, thresholds.dangerRows)) return "danger";
  if (distance <= rowStep * Math.max(0, thresholds.warningRows)) return "warning";
  return "none";
}

export function pressureBrickBottom(brick: PressureBrickSample): number {
  return typeof brick.bottom === "number" ? brick.bottom : brick.y + brick.height / 2;
}
